import type {
  AdminEvent,
  Entry,
  EventStats,
  HeatsResponse,
  Pagination,
  PublicEvent,
  StartLists,
  EntryStatus,
  PaymentStatus,
  Organiser,
  ImportPlan,
  LookupResult,
} from './types';

const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/api';

/** Carries the field-level messages the backend returns so forms can show them inline. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Supplies the admin access token. Registered by the auth provider so this
 * module does not need to know how tokens are obtained.
 */
type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider = async () => null;
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

interface RequestOptions {
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function authHeaders(auth: boolean | undefined): Promise<Record<string, string>> {
  if (!auth) return {};
  const token = await tokenProvider();
  if (!token) {
    throw new ApiError(401, 'unauthorized', 'Your session has expired. Please sign in again.');
  }
  return { authorization: `Bearer ${token}` };
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers: {
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(await authHeaders(options.auth)),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal ?? null,
    });
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } })
      ?.error;
    const details = error?.details as { fields?: Record<string, string> } | undefined;
    throw new ApiError(
      response.status,
      error?.code ?? 'error',
      error?.message ?? `Request failed (${response.status})`,
      details?.fields ?? {},
    );
  }

  return payload as T;
}

/** Fetches a CSV with auth and hands it to the browser as a download. */
async function downloadCsv(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(buildUrl(path), { headers: await authHeaders(true) });
  if (!response.ok) {
    throw new ApiError(response.status, 'export_failed', 'Could not generate the export.');
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const matched = /filename="?([^";]+)"?/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = matched?.[1] ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export interface RegistrationPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: string;
  club: string;
  categoryId: string;
  seedTime: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalNotes: string;
  consentRules: boolean;
  consentMedia: boolean;
}

export interface RegistrationResult {
  entry: {
    reference: string;
    firstName: string;
    lastName: string;
    email: string;
    status: EntryStatus;
    paymentStatus: PaymentStatus;
    seedTimeFormatted: string;
  };
  payment: { amountPence: number; currency: string; instructions: string };
}

export interface EntriesQuery {
  status?: string;
  paymentStatus?: string;
  categoryId?: string;
  q?: string;
  sort?: 'createdAt' | 'lastName' | 'seedTime' | 'reference';
  direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface HeatGenerationOptions {
  includeStatuses?: EntryStatus[];
  laneCount?: number;
  seedingMode?: 'lanes' | 'waves';
  balanceHeats?: boolean;
  groupByCategory?: boolean;
  heatDurationMinutes?: number;
  changeoverMinutes?: number;
  firstHeatAt?: string;
  dryRun?: boolean;
}

export const api = {
  // -- public ---------------------------------------------------------------
  getEvent: (signal?: AbortSignal) =>
    request<{ event: PublicEvent }>('GET', '/event', { signal }).then((r) => r.event),

  register: (payload: RegistrationPayload) =>
    request<RegistrationResult>('POST', '/registrations', { body: payload }),

  lookup: (reference: string, email: string) =>
    request<LookupResult>('POST', '/registrations/lookup', { body: { reference, email } }),

  startLists: (signal?: AbortSignal) => request<StartLists>('GET', '/start-lists', { signal }),

  searchStartLists: (q: string, signal?: AbortSignal) =>
    request<{
      results: {
        heatNumber: number;
        heatName: string;
        scheduledAt: string | null;
        lane: number;
        reference: string;
        firstName: string;
        lastName: string;
        club: string;
        seedTimeFormatted: string;
      }[];
    }>('GET', '/start-lists/search', { query: { q }, signal }),

  // -- admin ----------------------------------------------------------------
  admin: {
    getEvent: (signal?: AbortSignal) =>
      request<{ event: AdminEvent }>('GET', '/admin/event', { auth: true, signal }).then(
        (r) => r.event,
      ),

    updateEvent: (patch: Partial<AdminEvent>) =>
      request<{ event: AdminEvent }>('PATCH', '/admin/event', { auth: true, body: patch }).then(
        (r) => r.event,
      ),

    stats: (signal?: AbortSignal) =>
      request<{ stats: EventStats }>('GET', '/admin/stats', { auth: true, signal }).then(
        (r) => r.stats,
      ),

    listEntries: (query: EntriesQuery, signal?: AbortSignal) =>
      request<{ entries: Entry[]; pagination: Pagination }>('GET', '/admin/entries', {
        auth: true,
        query: query as Record<string, string | number | undefined>,
        signal,
      }),

    getEntry: (entryId: string, signal?: AbortSignal) =>
      request<{ entry: Entry }>('GET', `/admin/entries/${entryId}`, { auth: true, signal }).then(
        (r) => r.entry,
      ),

    createEntry: (payload: Record<string, unknown>) =>
      request<{ entry: Entry }>('POST', '/admin/entries', { auth: true, body: payload }).then(
        (r) => r.entry,
      ),

    updateEntry: (entryId: string, patch: Record<string, unknown>) =>
      request<{ entry: Entry; heatsStale: boolean }>('PATCH', `/admin/entries/${entryId}`, {
        auth: true,
        body: patch,
      }),

    deleteEntry: (entryId: string) =>
      request<void>('DELETE', `/admin/entries/${entryId}`, { auth: true }),

    bulkStatus: (entryIds: string[], patch: { status?: EntryStatus; paymentStatus?: PaymentStatus }) =>
      request<{ updated: number; failed: string[] }>('POST', '/admin/entries/bulk-status', {
        auth: true,
        body: { entryIds, ...patch },
      }),

    exportEntries: () => downloadCsv('/admin/entries/export.csv', 'entries.csv'),

    importTemplate: () =>
      downloadCsv('/admin/entries/import-template.csv', 'import-template.csv'),

    /**
     * Validates the file and reports what would happen. Writes nothing unless
     * `dryRun` is explicitly false.
     */
    importEntries: (csv: string, dryRun: boolean) =>
      request<ImportPlan>('POST', '/admin/entries/import', {
        auth: true,
        body: { csv, dryRun },
      }),

    // -- heats --------------------------------------------------------------
    getHeats: (signal?: AbortSignal) =>
      request<HeatsResponse>('GET', '/admin/heats', { auth: true, signal }),

    generateHeats: (options: HeatGenerationOptions) =>
      request<{ heats: HeatsResponse['heats']; meta: HeatsResponse['meta']; dryRun: boolean }>(
        'POST',
        '/admin/heats/generate',
        { auth: true, body: options },
      ),

    moveSwimmer: (entryId: string, toHeatNumber: number, toLane: number) =>
      request<{ heats: HeatsResponse['heats']; swapped: boolean }>('POST', '/admin/heats/move', {
        auth: true,
        body: { entryId, toHeatNumber, toLane },
      }),

    setHeatDuration: (heatNumber: number, durationMinutes: number) =>
      request<{ heats: HeatsResponse['heats'] }>('PATCH', `/admin/heats/${heatNumber}`, {
        auth: true,
        body: { durationMinutes },
      }),

    publishHeats: () =>
      request<{ published: boolean; publishedAt: string | null }>('POST', '/admin/heats/publish', {
        auth: true,
      }),

    unpublishHeats: () =>
      request<{ published: boolean; publishedAt: string | null }>(
        'POST',
        '/admin/heats/unpublish',
        { auth: true },
      ),

    clearHeats: () => request<{ cleared: boolean }>('DELETE', '/admin/heats', { auth: true }),

    exportHeats: () => downloadCsv('/admin/heats/export.csv', 'start-lists.csv'),

    // -- organisers ---------------------------------------------------------
    // Served by a separate Lambda whose role can reach Cognito but not the
    // entries table. Same origin and same JWT authorizer, so nothing changes
    // for the client.
    listOrganisers: (signal?: AbortSignal) =>
      request<{ organisers: Organiser[] }>('GET', '/admin/organisers', {
        auth: true,
        signal,
      }).then((r) => r.organisers),

    inviteOrganiser: (email: string) =>
      request<{ organisers: Organiser[]; created: boolean }>('POST', '/admin/organisers', {
        auth: true,
        body: { email },
      }),

    resendOrganiserInvite: (username: string) =>
      request<{ resent: boolean; email: string }>(
        'POST',
        `/admin/organisers/${encodeURIComponent(username)}/resend`,
        { auth: true },
      ),

    revokeOrganiser: (username: string) =>
      request<{ organisers: Organiser[] }>(
        'DELETE',
        `/admin/organisers/${encodeURIComponent(username)}`,
        { auth: true },
      ).then((r) => r.organisers),
  },
};
